import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiError } from '@/services/api';
import { useApplicants } from '@/contexts/ApplicantContext';
import { Card } from '@/components/ui/Card';
import { relativeTime } from '@/lib/format';
import type { NotificationSignal } from '@/services/types';

const KIND_TONE: Record<string, string> = {
  interview_invite: 'badge-blue',
  offer: 'badge-green',
  follow_up: 'badge-amber',
  rejection: 'badge-red',
  other: 'badge-gray',
};

export default function NotificationsPage() {
  const { current } = useApplicants();
  const [unseenOnly, setUnseenOnly] = useState(false);
  const [kindFilter, setKindFilter] = useState('');
  const [items, setItems] = useState<NotificationSignal[]>([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ applicant_id: current, limit: '100' });
      if (unseenOnly) params.set('unseen', '1');
      const r = await api.get<{ items: NotificationSignal[] }>(`/notifications?${params}`);
      setItems(r.items);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'load_failed');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, unseenOnly]);

  const filtered = kindFilter ? items.filter((i) => i.kind === kindFilter) : items;

  async function bulkDismiss() {
    const ids = filtered.filter((i) => !i.dismissed).map((i) => i.id);
    if (!ids.length) return;
    let n = 0;
    for (const id of ids) {
      try {
        await api.post(`/notifications/${id}/dismiss`);
        n++;
      } catch {
        /* count below */
      }
    }
    toast.success(`Dismissed ${n}/${ids.length}`);
    load();
  }

  async function mark(id: number, kind: 'seen' | 'dismiss') {
    try {
      await api.post(`/notifications/${id}/${kind}`);
      load();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : `${kind}_failed`);
    }
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-semibold tracking-tight">Notifications</h2>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-1.5 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={unseenOnly}
              onChange={(e) => setUnseenOnly(e.target.checked)}
            />
            Unseen only
          </label>
          <select
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value)}
            className="input w-40"
          >
            <option value="">All kinds</option>
            <option value="interview_invite">Interview invites</option>
            <option value="offer">Offers</option>
            <option value="follow_up">Follow-ups</option>
            <option value="rejection">Rejections</option>
            <option value="other">Other</option>
          </select>
          {filtered.length > 0 && (
            <button onClick={bulkDismiss} className="btn-secondary">
              Dismiss all visible
            </button>
          )}
        </div>
      </header>

      <Card>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-gray-500">No notifications.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {filtered.map((s) => (
              <li key={s.id} className="flex items-start gap-3 py-3">
                <Bell className={`mt-1 h-4 w-4 ${s.seen ? 'text-gray-300' : 'text-red-500'}`} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={KIND_TONE[s.kind ?? 'other']}>{s.kind ?? 'other'}</span>
                    <span className="font-medium text-gray-800">{s.company ?? '—'}</span>
                    {s.job_id && (
                      <Link
                        to={`/applications/${s.job_id}`}
                        className="text-xs text-primary-600 hover:underline"
                      >
                        view job #{s.job_id}
                      </Link>
                    )}
                    <span className="ml-auto text-xs text-gray-400">
                      {relativeTime(s.received_at ?? s.created_at)}
                    </span>
                  </div>
                  <p className="mt-0.5 text-sm text-gray-700">{s.raw_subject}</p>
                  {s.raw_snippet && (
                    <p className="mt-1 line-clamp-2 text-xs text-gray-500">{s.raw_snippet}</p>
                  )}
                </div>
                <div className="flex gap-1">
                  {!s.seen && (
                    <button
                      onClick={() => mark(s.id, 'seen')}
                      title="Mark seen"
                      className="rounded p-1 text-gray-500 hover:bg-gray-100"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={() => mark(s.id, 'dismiss')}
                    title="Dismiss"
                    className="rounded p-1 text-gray-500 hover:bg-gray-100"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
